import {FC, useState, useEffect, useCallback} from 'react'
import {Modal, Button, Form} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import axios from 'axios'
import {Toast, AlertBoostrapButtons} from '../../../../../../_metronic/partials/widgets'
import {getProjectById} from '../../../components/core/_requests'

/** 422 de regra de plano/limite (não validação de formulário). */
const isPortalPlanOrLimitError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false
  }
  const status = error.response?.status
  const data = error.response?.data as {message?: string; errors?: unknown} | undefined
  if (status === 404) {
    return true
  }
  if (status !== 422 || data?.errors) {
    return false
  }
  const message = (data?.message || '').toLowerCase()
  return (
    message.includes('portal') ||
    message.includes('plano') ||
    message.includes('upgrade') ||
    message.includes('limite')
  )
}

const getInviteSubmitErrorMessage = (error: unknown): string => {
  if (isPortalPlanOrLimitError(error)) {
    return 'O Portal do Cliente não está disponível no seu plano atual. Faça upgrade para convidar clientes por e-mail e deixá-los acompanhar prazos, cronograma e entregas em um ambiente exclusivo.'
  }
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as {message?: string; errors?: Record<string, string[]>}
    if (data?.message) {
      return data.message
    }
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0]
      if (first) {
        return first
      }
    }
  }
  return 'Ocorreu um erro ao processar os convites.'
}

type Props = {
  show: boolean
  handleClose: () => void
  projectId?: number
  projectName?: string
  clientId?: number
  clientName?: string
}

const ProjectPortalInviteModal: FC<Props> = ({
  show,
  handleClose,
  projectId,
  projectName,
  clientId: propClientId,
  clientName: propClientName,
}) => {
  const intl = useIntl()
  const [emails, setEmails] = useState<string>('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [clientId, setClientId] = useState<number | undefined>(propClientId)
  const [clientName, setClientName] = useState<string | undefined>(propClientName)
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false)
  const [isSavingBlocks, setIsSavingBlocks] = useState(false)
  const [accessUsers, setAccessUsers] = useState<
    Array<{user_id: number; email: string; name: string; created_at: string}>
  >([])
  const [projectType, setProjectType] = useState<number | undefined>(undefined)
  const [visibilitySettings, setVisibilitySettings] = useState({
    show_deadline: true,
    show_cycle_info: true,
    show_schedule: true,
    show_tasks: true,
  })
  const [results, setResults] = useState<{
    sent: Array<{email: string; message: string}>
    already_registered: Array<{email: string; message: string}>
    errors: Array<{email: string; message: string}>
  } | null>(null)

  const fetchAccessUsers = useCallback(async () => {
    if (!projectId) return

    try {
      setIsLoadingUsers(true)
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000'
      const token = localStorage.getItem('token')

      const response = await axios.get(`${apiUrl}/api/projetos/${projectId}/portal-access-users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.data.success) {
        setAccessUsers(response.data.data || [])
      }
    } catch (error: any) {
    } finally {
      setIsLoadingUsers(false)
    }
  }, [projectId])

  // Buscar clientId do projeto e lista de usuários com acesso
  useEffect(() => {
    if (!show) {
      setIsLoadingUsers(false)
      return
    }

    if (!projectId) {
      return
    }

    setSubmitError(null)

    if (propClientId && propClientId !== 0) {
      setClientId(propClientId)
      setClientName(propClientName)
    } else {
      getProjectById(projectId)
        .then((project) => {
          const projectClientId =
            project.clientId || (project.client_id ? parseInt(project.client_id) : undefined)
          if (projectClientId) {
            setClientId(projectClientId)
          }
          if (project.clientName) {
            setClientName(project.clientName)
          }
        })
        .catch(() => {
          // Modal permanece utilizável; clientId pode vir de nova tentativa ou props.
        })
    }

    fetchAccessUsers()
    fetchPublicPageBlocks()
  }, [show, projectId, propClientId, propClientName, fetchAccessUsers])

  const fetchPublicPageBlocks = useCallback(async () => {
    if (!projectId) return

    try {
      setIsLoadingBlocks(true)
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000'
      const token = localStorage.getItem('token')

      // Buscar projeto para obter as configurações de blocos
      const project = await getProjectById(projectId)
      if (project) {
        setProjectType(project.type)
        setVisibilitySettings({
          show_deadline: project.show_deadline !== undefined ? project.show_deadline : true,
          show_cycle_info: project.show_cycle_info !== undefined ? project.show_cycle_info : true,
          show_schedule: project.show_schedule !== undefined ? project.show_schedule : true,
          show_tasks: project.show_tasks !== undefined ? project.show_tasks : true,
        })
      }
    } catch (error: any) {
    } finally {
      setIsLoadingBlocks(false)
    }
  }, [projectId])

  const saveVisibilitySettings = async (newSettings?: typeof visibilitySettings) => {
    if (!projectId) return

    const settingsToSave = newSettings || visibilitySettings

    try {
      setIsSavingBlocks(true)
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000'
      const token = localStorage.getItem('token')

      await axios.put(
        `${apiUrl}/api/projetos/${projectId}/public-page-blocks`,
        {
          show_deadline: settingsToSave.show_deadline,
          show_cycle_info: settingsToSave.show_cycle_info,
          show_schedule: settingsToSave.show_schedule,
          show_tasks: settingsToSave.show_tasks,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      // Toast silencioso para auto-save (sem mostrar notificação)
    } catch (error: any) {
      Toast.fire({
        icon: 'error',
        title: 'Erro ao salvar configurações',
        text: error.response?.data?.message || 'Ocorreu um erro ao salvar as configurações',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
    } finally {
      setIsSavingBlocks(false)
    }
  }

  const handleVisibilityChange = async (field: keyof typeof visibilitySettings, value: boolean) => {
    const newSettings = {
      ...visibilitySettings,
      [field]: value,
    }
    setVisibilitySettings(newSettings)
    // Auto-save
    await saveVisibilitySettings(newSettings)
  }

  const handleRemoveAccess = async (userId: number, userEmail: string, userName?: string) => {
    if (!projectId) return

    const result = await AlertBoostrapButtons.fire({
      title: 'Remover acesso?',
      html: `
        <div class="text-start">
          <p class="mb-2">Tem certeza que deseja remover o acesso de:</p>
          <p class="fw-bold text-dark mb-1">${userName || userEmail}</p>
          <p class="text-muted small mb-0">${userEmail}</p>
          <p class="text-danger mt-3 mb-0"><small>Esta ação pode ser revertida adicionando o acesso novamente.</small></p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, remover acesso',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-light',
      },
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000'
      const token = localStorage.getItem('token')

      const response = await axios.delete(`${apiUrl}/api/projetos/${projectId}/portal-access`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        data: {
          user_id: userId,
        },
      })

      if (response.data.success) {
        Toast.fire({
          icon: 'success',
          title: 'Acesso removido com sucesso',
          text: `O acesso de ${userEmail} foi removido.`,
          customClass: {
            popup: 'bg-light-success',
            title: 'text-success',
          },
        })

        // Atualizar lista de usuários
        fetchAccessUsers()
      }
    } catch (error: any) {
      Toast.fire({
        icon: 'error',
        title: 'Erro ao remover acesso',
        text: error.response?.data?.message || 'Ocorreu um erro ao remover o acesso',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!clientId || clientId === 0) {
      Toast.fire({
        icon: 'error',
        title: 'Cliente não encontrado',
        text: 'Não foi possível identificar o cliente deste projeto. Tente novamente ou use a opção na página de clientes.',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
      return
    }

    // Parse emails from textarea (split by newline or comma)
    const emailList = emails
      .split(/[\n,;]/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0 && email.includes('@'))

    if (emailList.length === 0) {
      Toast.fire({
        icon: 'error',
        title: 'Nenhum email válido encontrado',
        text: 'Por favor, insira pelo menos um email válido',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
      return
    }

    setIsSending(true)
    setResults(null)
    setSubmitError(null)

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000'
      const token = localStorage.getItem('token')

      // Usar o novo endpoint de projetos ao invés de clientes
      const response = await axios.post(
        `${apiUrl}/api/projetos/${projectId}/send-portal-invites`,
        {emails: emailList},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (response.data.success) {
        setResults(response.data.results)

        const totalSent = response.data.results.sent.length
        const totalRegistered = response.data.results.already_registered.length
        const totalErrors = response.data.results.errors.length

        // Construir mensagem dinamicamente baseado no que realmente aconteceu
        const messageParts: string[] = []

        if (totalSent > 0) {
          messageParts.push(
            `${totalSent} ${totalSent === 1 ? 'convite enviado' : 'convites enviados'}`
          )
        }

        if (totalRegistered > 0) {
          messageParts.push(
            `${totalRegistered} ${
              totalRegistered === 1 ? 'acesso adicionado' : 'acessos adicionados'
            } automaticamente`
          )
        }

        if (totalErrors === 0) {
          if (messageParts.length > 0) {
            Toast.fire({
              icon: 'success',
              title: 'Processamento concluído com sucesso!',
              text: messageParts.join(' e ') + '.',
              customClass: {
                popup: 'bg-light-success',
                title: 'text-success',
              },
            })
          } else {
            // Caso raro: nenhum convite enviado e nenhum acesso adicionado, mas sem erros
            Toast.fire({
              icon: 'info',
              title: 'Processamento concluído',
              text: 'Nenhuma ação foi necessária.',
              customClass: {
                popup: 'bg-light-info',
                title: 'text-info',
              },
            })
          }
        } else {
          const errorText = `${totalErrors} ${totalErrors === 1 ? 'erro' : 'erros'}`
          const successText = messageParts.length > 0 ? messageParts.join(', ') + ', ' : ''

          Toast.fire({
            icon: 'warning',
            title: 'Processamento concluído com alguns erros',
            text: `${successText}${errorText}.`,
            customClass: {
              popup: 'bg-light-warning',
              title: 'text-warning',
            },
          })
        }

        // Atualiza a lista sempre que houver qualquer inclusão efetiva.
        if (totalSent > 0 || totalRegistered > 0) {
          fetchAccessUsers()
        }

        // Limpar campo de emails apenas quando não houver erros.
        if (totalErrors === 0) {
          setEmails('')
        }
      }
    } catch (error: unknown) {
      setSubmitError(getInviteSubmitErrorMessage(error))
    } finally {
      setIsSending(false)
    }
  }

  const handleCloseModal = () => {
    setEmails('')
    setResults(null)
    setSubmitError(null)
    handleClose()
  }

  return (
    <Modal show={show} onHide={handleCloseModal} size='lg' centered dialogClassName='task-modal-content'>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className='bi bi-envelope-paper me-2'></i>
          Compartilhar projeto
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
          <>
            <div className='mb-4'>
              {projectName && (
                <p className='fw-bold text-dark mb-1'>
                  <i className='bi bi-folder me-2'></i>
                  Projeto: {projectName}
                </p>
              )}
              {clientName && (
                <p className='fw-bold text-dark mb-0'>
                  <i className='bi bi-building me-2'></i>
                  Cliente: {clientName}
                </p>
              )}
            </div>

            {/* Lista de usuários com acesso */}
            <div className='mb-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h6 className='mb-0 fw-bold text-gray-800'>
                  <i className='bi bi-people me-2 text-primary'></i>
                  Usuários com acesso ({accessUsers.length})
                </h6>
              </div>

              {isLoadingUsers ? (
                <div className='text-center py-4'>
                  <div className='spinner-border spinner-border-sm text-primary' role='status'>
                    <span className='visually-hidden'>Carregando...</span>
                  </div>
                  <p className='text-muted mt-2 mb-0 small'>Carregando usuários...</p>
                </div>
              ) : accessUsers.length === 0 ? (
                <div className='alert alert-light-info border border-info border-dashed mb-0'>
                  <div className='d-flex align-items-center'>
                    <i className='bi bi-info-circle text-info fs-3 me-3'></i>
                    <div>
                      <div className='fw-semibold text-gray-800'>Nenhum usuário com acesso</div>
                      <div className='text-muted small'>
                        Adicione usuários abaixo para compartilhar o projeto.
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='d-flex flex-column gap-1'>
                  {accessUsers.map((user) => (
                    <div
                      key={user.user_id}
                      className='d-flex justify-content-between align-items-center py-2 px-3 border border-gray-300 border-dashed rounded bg-light'
                    >
                      <div className='flex-grow-1 text-gray-800'>
                        <span className='fw-semibold'>{user.name || 'Sem nome'}</span>
                        <span className='text-muted mx-2'>-</span>
                        <span className='text-muted'>{user.email}</span>
                      </div>
                      <button
                        className='btn btn-sm btn-icon btn-light-danger ms-2'
                        onClick={() => handleRemoveAccess(user.user_id, user.email, user.name)}
                        title='Remover acesso'
                      >
                        <i className='bi bi-trash fs-6'></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className='separator my-4'></div>

            {/* Configurações de Blocos Públicos */}
            <div className='mb-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <Form.Label className='fw-bold mb-0'>
                  <i className='bi bi-eye me-2 text-primary'></i>
                  Blocos a exibir na página pública:
                </Form.Label>
                {isSavingBlocks && (
                  <span className='text-muted small'>
                    <span
                      className='spinner-border spinner-border-sm me-2'
                      role='status'
                      aria-hidden='true'
                    ></span>
                    Salvando...
                  </span>
                )}
              </div>

              {isLoadingBlocks ? (
                <div className='text-center py-3'>
                  <div className='spinner-border spinner-border-sm text-primary' role='status'>
                    <span className='visually-hidden'>Carregando...</span>
                  </div>
                </div>
              ) : (
                <div className='d-flex flex-column gap-2'>
                  <Form.Check
                    type='checkbox'
                    id='show_deadline'
                    label={<span style={{cursor: 'pointer'}}>Prazo do projeto</span>}
                    checked={visibilitySettings.show_deadline}
                    onChange={(e) => handleVisibilityChange('show_deadline', e.target.checked)}
                    disabled={isSavingBlocks}
                    role='button'
                    style={{cursor: 'pointer'}}
                  />
                  {projectType === 0 && (
                    <Form.Check
                      type='checkbox'
                      id='show_cycle_info'
                      label={<span style={{cursor: 'pointer'}}>Informações do ciclo</span>}
                      checked={visibilitySettings.show_cycle_info}
                      onChange={(e) => handleVisibilityChange('show_cycle_info', e.target.checked)}
                      disabled={isSavingBlocks}
                      role='button'
                      style={{cursor: 'pointer'}}
                    />
                  )}
                  <Form.Check
                    type='checkbox'
                    id='show_schedule'
                    label={<span style={{cursor: 'pointer'}}>Cronograma</span>}
                    checked={visibilitySettings.show_schedule}
                    onChange={(e) => handleVisibilityChange('show_schedule', e.target.checked)}
                    disabled={isSavingBlocks}
                    role='button'
                    style={{cursor: 'pointer'}}
                  />
                  <Form.Check
                    type='checkbox'
                    id='show_tasks'
                    label={<span style={{cursor: 'pointer'}}>Tarefas (Kanban)</span>}
                    checked={visibilitySettings.show_tasks}
                    onChange={(e) => handleVisibilityChange('show_tasks', e.target.checked)}
                    disabled={isSavingBlocks}
                    role='button'
                    style={{cursor: 'pointer'}}
                  />
                </div>
              )}
            </div>

            <div className='separator my-4'></div>

            <Form onSubmit={handleSubmit}>
              <Form.Group className='mb-4'>
                <Form.Label>
                  <strong>Emails</strong>
                  <span className='text-danger ms-1'>*</span>
                  <span className='text-muted ms-2'>
                    (um por linha, separados por vírgula ou ponto e vírgula)
                  </span>
                </Form.Label>
                <Form.Control
                  as='textarea'
                  rows={6}
                  value={emails}
                  onChange={(e) => {
                    setEmails(e.target.value)
                    if (submitError) {
                      setSubmitError(null)
                    }
                  }}
                  placeholder='email1@exemplo.com&#10;email2@exemplo.com&#10;email3@exemplo.com'
                  disabled={isSending}
                />
                <Form.Text className='text-muted'>
                  Insira os emails dos usuários que devem receber acesso ao Portal do Cliente. Se o
                  usuário já estiver registrado, o acesso será adicionado automaticamente.
                </Form.Text>
              </Form.Group>

              {submitError && (
                <div className='alert alert-danger mb-4' role='alert'>
                  <strong>
                    <i className='bi bi-exclamation-triangle me-2'></i>
                    Não foi possível compartilhar
                  </strong>
                  <p className='mb-0 mt-2'>{submitError}</p>
                </div>
              )}

              {results && (
                <div className='mb-4'>
                  {results.sent.length > 0 && (
                    <div className='alert alert-success mb-2'>
                      <strong>
                        <i className='bi bi-check-circle me-2'></i>
                        {results.sent.length} Convite(s) Enviado(s):
                      </strong>
                      <ul className='mb-0 mt-2'>
                        {results.sent.map((item, index) => (
                          <li key={index}>
                            {item.email} - {item.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {results.already_registered.length > 0 && (
                    <div className='alert alert-info mb-2'>
                      <strong>
                        <i className='bi bi-info-circle me-2'></i>
                        {results.already_registered.length} Acesso(s) Adicionado(s):
                      </strong>
                      <ul className='mb-0 mt-2'>
                        {results.already_registered.map((item, index) => (
                          <li key={index}>
                            {item.email} - {item.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {results.errors.length > 0 && (
                    <div className='alert alert-danger mb-2'>
                      <strong>
                        <i className='bi bi-exclamation-triangle me-2'></i>
                        {results.errors.length} Erro(s):
                      </strong>
                      <ul className='mb-0 mt-2'>
                        {results.errors.map((item, index) => (
                          <li key={index}>
                            {item.email} - {item.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className='d-flex justify-content-end gap-2'>
                <Button variant='secondary' onClick={handleCloseModal} disabled={isSending}>
                  Fechar
                </Button>
                <Button
                  variant='primary'
                  type='submit'
                  disabled={
                    isSending || !emails.trim() || !clientId || clientId === 0
                  }
                >
                  {isSending ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                        aria-hidden='true'
                      ></span>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className='bi bi-send me-2'></i>
                      Compartilhar projeto
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </>
      </Modal.Body>
    </Modal>
  )
}

export {ProjectPortalInviteModal}
